import React, { useEffect, useState, useRef } from 'react';
import { InvoiceStatusBadge } from './invoice-status-badge.component';
import { DashboardButton } from './dashboard-button.component';
import { useUserDashboard } from '../../hooks/useUserDashboard';
import {
  TableRow,
  TableCell,
  TableHeader,
  TableColumn,
  MobileCardRow,
  MobileCardWrapper,
  TableBody,
} from './table-components';
import './../../scss/components/invoice-table.style.scss';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { NoInvoicesYet } from './no-invoices-yet.component';
import { formatAmount } from '../../helper/common.helper';
import LoadingCard from '../../components/SkeletonLoading'

// Interface matching the actual API response
interface InvoiceData {
  id: number;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  dueDate: string;
  invoiceStatus: number;
}

const mapInvoiceStatus = (status: number): 'paid' | 'not-paid' | 'overdue' => {
  switch (status) {
    case 2:
      return 'paid';
    case 3:
      return 'overdue';
    case 1:
    default:
      return 'not-paid';
  }
};


// Helper function to format date
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('sv-SE');
};

interface InvoiceTableProps {
  className?: string;
}

const ChevronDownIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowLeftIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M10 12L6 8L10 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M6 4L10 8L6 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const InvoiceTable: React.FC<InvoiceTableProps> = ({
  className = ''
}) => {
  const {
    dashboardData,
    loading,
    error,
    pagination,
    fetchDashboardData,
    goToNextPage,
    goToPreviousPage,
    setCurrentPage,
    setDateTime,
    clearDateTime
  } = useUserDashboard(5);
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  // State for year dropdown
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear, currentYear - 1];
  const invoices = dashboardData?.response || [];

  // Initialize DateTime filter on mount and clear on unmount
  useEffect(() => {
    const currentYearDate = `${currentYear}-01-01T10:44:25+07:00`;
    setDateTime(currentYearDate);
    setSelectedYear(currentYear);

    // Cleanup: Clear DateTime filter when component unmounts
    return () => {
      clearDateTime();
    };
  }, [currentYear, setDateTime, clearDateTime]);

  useEffect(() => {
    const currentYearDate = `${selectedYear}-01-01T10:44:25+07:00`;
    setDateTime(currentYearDate);
    setCurrentPage(1);
  }, [selectedYear, setDateTime, setCurrentPage]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchDashboardData();
  }, [pagination.currentPage, pagination.pageSize, fetchDashboardData]);

  const handlePrevPage = () => {
    goToPreviousPage();
  };

  const handleNextPage = () => {
    goToNextPage();
  };

  const handleGoToPage = (page: number) => {
    setCurrentPage(page);
  };

  // Handle year selection
  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setIsDropdownOpen(false);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const getPageNumbers = () => {
    const { currentPage, totalPages } = pagination;
    const pages: (number | string)[] = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className={`invoice-table-container ${className}`}>
        <div className="invoice-table-header">
          <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
        </div>
        <div className="invoice-table-content">
          <LoadingCard/>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`invoice-table-container ${className}`}>
        <div className="invoice-table-header">
          <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
        </div>
        <div className="invoice-table-content">
          <div className="error-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#dc3545'
          }}>
            Error loading invoices: {error}
          </div>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className={`invoice-table-container ${className}`}>
        <div className="invoice-table-header">
          <div className='d-flex justify-content-between align-items-center w-100'>
            <div>
              <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
              <p>{t('YOUR_CREATED_INVOICES_WILL_BE_SHOWN_HERE')}</p>
            </div>
                    <div className="year-filter-dropdown " ref={dropdownRef}>
            <button
              className="year-filter-button"
              onClick={toggleDropdown}
              type="button"
            >
              <span>{selectedYear}</span>
              <ChevronDownIcon />
            </button>

            {isDropdownOpen && (
              <div className="year-dropdown-menu">
                {availableYears.map((year) => (
                  <button
                    key={year}
                    className={`year-dropdown-item ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => handleYearSelect(year)}
                    type="button"
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>

        </div>
        <div className="invoice-table-content">
          <div className="empty-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
          }}>
            <div className='d-flex justify-content-center align-items-center flex-column gap-3'>
              <NoInvoicesYet />

              {t('NO_INVOICES_YET')}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`invoice-table-container ${className}`}>
      <div className="invoice-table-header">
        <div className='d-flex justify-content-between align-items-center w-100'>
        <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>

        {/* Year Filter Dropdown */}
        <div className="year-filter-dropdown" ref={dropdownRef}>
          <button
            className="year-filter-button"
            onClick={toggleDropdown}
            type="button"
          >
            <span>{selectedYear}</span>
            <ChevronDownIcon />
          </button>

          {isDropdownOpen && (
            <div className="year-dropdown-menu">
              {availableYears.map((year) => (
                <button
                  key={year}
                  className={`year-dropdown-item ${selectedYear === year ? 'active' : ''}`}
                  onClick={() => handleYearSelect(year)}
                  type="button"
                >
                  {year}
                </button>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>

      <div className="invoice-table-content">
        <div className="invoice-table-scroll-wrapper">
          <div className="invoice-table">
            {!isMobile && (
              <TableHeader>
                <TableColumn>{t('INVOICE_NO')}</TableColumn>
                <TableColumn>{t('CLIENT_NAME')}</TableColumn>
                <TableColumn>{t('AMOUNT')}</TableColumn>
                <TableColumn>{t('DUE_DATE')}</TableColumn>
                <TableColumn>{t('STATUS')}</TableColumn>
              </TableHeader>
            )}

            <TableBody>
              {isMobile ? (
                // Mobile: Card-based layout
                invoices.map((invoice) => (
                  <MobileCardWrapper key={invoice.id}>
                    <MobileCardRow label={t('INVOICE_NO')}>
                      <span className="invoice-table__invoice-number">{invoice.invoiceNumber}</span>
                    </MobileCardRow>
                    <MobileCardRow label={t('CLIENT_NAME')}>
                      {invoice.customer?.name || invoice.clientName}
                    </MobileCardRow>
                    <MobileCardRow label={t('AMOUNT')}>
                      {formatAmount(invoice.amount)}
                    </MobileCardRow>
                    <MobileCardRow label={t('DUE_DATE')}>
                      {formatDate(invoice.dueDate)}
                    </MobileCardRow>
                    <MobileCardRow label={t('STATUS')}>
                      <InvoiceStatusBadge status={mapInvoiceStatus(invoice.invoiceStatus)} />
                    </MobileCardRow>
                  </MobileCardWrapper>
                ))
              ) : (
                // Desktop: Traditional table row layout
                invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <span className="invoice-table__invoice-number">{invoice.invoiceNumber}</span>
                    </TableCell>
                    <TableCell>
                      {invoice.customer?.name || invoice.clientName}
                    </TableCell>
                    <TableCell>
                      {formatAmount(invoice.amount)}
                    </TableCell>
                    <TableCell>
                      {formatDate(invoice.dueDate)}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusBadge status={mapInvoiceStatus(invoice.invoiceStatus)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </div>
        </div>

        <div className="invoice-table-pagination">
          <div className="pagination-controls">
            <button
              className="pagination-button pagination-button--prev"
              onClick={handlePrevPage}
              disabled={pagination.currentPage === 1}
            >
              <ArrowLeftIcon />
              <span>{t('PREV_STEP')}</span>
            </button>

            {/* Render page numbers */}
            {getPageNumbers()?.map((page, index) => {
              if (page === '...') {
                return (
                  <button
                    key={`ellipsis-${index}`}
                    className="pagination-button pagination-button--number"
                    disabled
                  >
                    ...
                  </button>
                );
              }
              return (
                <button
                  key={page}
                  className={`pagination-button pagination-button--number${pagination.currentPage === page ? ' pagination-button--active' : ''}`}
                  onClick={() => handleGoToPage(page as number)}
                >
                  {page}
                </button>
              );
            })}

            <button
              className="pagination-button pagination-button--next"
              onClick={handleNextPage}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              <span>{t('NEXT_STEP')}</span>
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};